import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { getUserDetails } from '../../../utils';

const SUPPORT_EMAIL = 'support@edifyingteachers.com';

function normalizeRole(role) {
  return String(role || '').toUpperCase();
}

function getHelpCenterBaseUrl() {
  // Configure this in env (recommended):
  // REACT_APP_HELP_CENTER_URL=https://<your-help-site>
  return process.env.REACT_APP_HELP_CENTER_URL || '';
}

function openExternal(url) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function getSuggestedDocSlug(pathname) {
  // Minimal route → doc mapping (can be expanded anytime)
  const map = {
    '/dashboard/app': 'dashboard',
    '/dashboard/my-profile': 'profile',
    '/dashboard/meeting-invites': 'mentoring-session-invites',
    '/dashboard/upcoming-events': 'upcoming-mentoring-sessions',
    '/dashboard/timeslots': 'availability',
    '/dashboard/find-edifiers': 'find-an-edifier',
    '/dashboard/paired': 'your-edifiers',
    '/dashboard/teachers': 'your-mentees',
    '/dashboard/district-users': 'manage-users',
    '/dashboard/manage-pairings': 'pairing-management',
    '/dashboard/requests': 'pairing-update-requests',
    '/dashboard/find-schools': 'partnership-sites',
    '/dashboard/manage-principals': 'manage-principals',
    '/dashboard/manage-credentials': 'manage-credentials',
    '/dashboard/send-notifications': 'send-notifications',
    '/dashboard/quote-management': 'quote-management',
    '/dashboard/principal/dashboard': 'principal-dashboard',
    '/dashboard/principal/users': 'principal-users',
    '/dashboard/principal/pairings': 'principal-pairings',
  };

  return map[pathname] || 'getting-started';
}

export default function HelpPopover() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userDetails = getUserDetails();
  const role = normalizeRole(userDetails?.role);

  const helpBase = getHelpCenterBaseUrl();
  const suggestedSlug = getSuggestedDocSlug(pathname);

  const [open, setOpen] = useState(null);
  const [query, setQuery] = useState('');

  const handleOpen = (event) => setOpen(event.currentTarget);
  const handleClose = () => setOpen(null);

  const quickLinks = useMemo(
    () => ([
      {
        id: 'faqs',
        label: 'FAQs',
        icon: <LiveHelpOutlinedIcon sx={{ fontSize: 20 }} />,
        internal: '/faqs', // Link to internal FAQs page
      },
      {
        id: 'contact-support',
        label: 'Contact Support',
        icon: <SupportAgentOutlinedIcon sx={{ fontSize: 20 }} />,
        external: `mailto:${SUPPORT_EMAIL}`,
      },
    ]),
    [helpBase, role]
  );

  const suggestedLink = useMemo(
    () => ({
      label: 'Suggested for this page',
      description: pathname,
      external: helpBase ? `${helpBase}/features/${suggestedSlug}` : '',
    }),
    [helpBase, pathname, suggestedSlug]
  );

  const handleRestartTour = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return;
      const u = JSON.parse(raw);
      u.hasCompletedTour = false;
      localStorage.setItem('user', JSON.stringify(u));
      // Reload so DashboardLayout can re-evaluate and trigger the tour
      window.location.reload();
    } catch (e) {
      // no-op; UI only
    }
  };

  const isHelpConfigured = Boolean(helpBase);

  return (
    <>
      <Tooltip title="Help">
        <IconButton
          color={open ? 'primary' : 'default'}
          onClick={handleOpen}
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'primary.lighter',
            color: 'primary.main',
            '&:hover': { bgcolor: 'primary.light' },
          }}
          data-tour="header-help"
        >
          <HelpOutlineIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: 360,
            maxWidth: '92vw',
            p: 2,
            borderRadius: 2,
          },
        }}
      >
        <Stack spacing={1.5}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Help
            </Typography>

            {role ? (
              <Chip
                size="small"
                label={role === 'COACH' ? 'Edifier' : role}
                sx={{ fontWeight: 700 }}
              />
            ) : null}
          </Box>

         

          

          

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
            Important links
          </Typography>

          {/* Visit Help Center - Moved to top */}
          <List dense disablePadding>
            <ListItemButton
              component="a"
              href="/help"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              sx={{
                borderRadius: 1,
                backgroundColor: 'primary.lighter',
                mb: 1,
                '&:hover': {
                  backgroundColor: 'primary.light',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                <HelpOutlineIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Visit Help Center"
                secondary="Opens in new tab"
                primaryTypographyProps={{ fontWeight: 700, fontSize: 13, color: 'primary.main' }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
              <ChevronRightIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            </ListItemButton>

            {/* Quick Links (FAQs, etc.) */}
            {quickLinks.map((l) => (
              <ListItemButton
                key={l.id}
                onClick={() => {
                  if (l.external) {
                    openExternal(l.external);
                  } else if (l.internal) {
                    window.open(l.internal, '_blank', 'noopener,noreferrer');
                    handleClose();
                  }
                }}
                disabled={!l.external && !l.internal}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>{l.icon}</ListItemIcon>
                <ListItemText primary={l.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 13 }} />
                <LaunchIcon sx={{ fontSize: 18, opacity: (l.external || l.internal) ? 0.8 : 0.3 }} />
              </ListItemButton>
            ))}
          </List>

          <Divider />

          <List dense disablePadding>

            <ListItemButton
              onClick={() => {
                handleClose();
                navigate('/dashboard/my-profile');
              }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                <ArticleOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary="Go to Profile"
                primaryTypographyProps={{ fontWeight: 600, fontSize: 13 }}
              />
            </ListItemButton>

            <ListItemButton onClick={handleRestartTour} sx={{ borderRadius: 1 }}>
              <ListItemIcon sx={{ minWidth: 34 }}>
                <RestartAltOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary="Restart onboarding tour"
                secondary="Replays the guided tour"
                primaryTypographyProps={{ fontWeight: 600, fontSize: 13 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </ListItemButton>
          </List>
        </Stack>
      </Popover>
    </>
  );
}

